import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { DEFAULT_LANG, SHOW_LANG_TOGGLE, Lang } from '../config/locale';
import { formatPrice as formatPriceByLang } from '../utils/format';

interface HeaderProps {
  totalPrice?: number;
  onQuoteClick?: () => void;
  onResetClick?: () => void;
  onLogoClick?: () => void;
  showReset?: boolean;
  isFinalPage?: boolean;
  showPrice?: boolean;
  lang?: Lang;
  onLangChange?: (lang: Lang) => void;
}

const Header: React.FC<HeaderProps> = ({
  totalPrice = 0,
  onQuoteClick,
  onResetClick,
  onLogoClick,
  showReset,
  isFinalPage,
  showPrice = false,
  lang = DEFAULT_LANG,
  onLangChange,
}) => {
  const isKo = lang === 'ko';
  const formatPrice = (price: number) => formatPriceByLang(price, lang);

  return (
    <Wrapper>
      <Inner>
        <LeftArea>
          <Logo onClick={onLogoClick || onResetClick} style={{ cursor: 'pointer' }}>
            <LogoImg src={isKo ? "/logo.jpg" : "/logo-en.svg"} alt={isKo ? "우수건설기계" : "Woosu Machinery Co. Ltd"} />
          </Logo>
          {showReset && (
            <>
              <Divider />
              <ResetButton onClick={onResetClick}>{isKo ? '처음부터 다시' : 'Start Over'}</ResetButton>
            </>
          )}
        </LeftArea>
        <RightArea>
          {/* 2026-09-18 영문 사이트에서 최종 금액·쇼핑몰 버튼을 제거했다(견적 전용).
              2026-10-31 한국어를 다시 열면서 한국어 화면에만 원래대로 되살렸다. */}
          {isKo && showPrice && (
            <PriceArea>
              <PriceLabel>최종 견적 금액</PriceLabel>
              <PriceValue>
                {totalPrice > 0 ? formatPrice(totalPrice) : '가격 문의'}
              </PriceValue>
            </PriceArea>
          )}
          {!isFinalPage && (
            <QuoteButton onClick={onQuoteClick}>
              {isKo ? '견적서 받기' : 'Get Quote'}
            </QuoteButton>
          )}
          {isFinalPage && isKo && (
            <QuoteButton onClick={() => window.open('https://xn--z69azfl4br08b28au7o.com/', '_blank')}>
              쇼핑몰 바로가기
            </QuoteButton>
          )}
          {SHOW_LANG_TOGGLE && (
            <LangToggle>
              <LangBtn $active={lang === 'ko'} onClick={() => onLangChange?.('ko')}>KR</LangBtn>
              <LangBtn $active={lang === 'en'} onClick={() => onLangChange?.('en')}>EN</LangBtn>
            </LangToggle>
          )}
        </RightArea>
      </Inner>
    </Wrapper>
  );
};

const Wrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.border};
  height: ${theme.layout.headerHeight};
`;

const Inner = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImg = styled.img`
  height: 36px;
  object-fit: contain;
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: ${theme.colors.border};
`;

const ResetButton = styled.button`
  background: ${theme.colors.white};
  border: 1.5px solid ${theme.colors.primary};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.primaryDark};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: ${theme.colors.primaryLight};
  }
`;

const RightArea = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const PriceArea = styled.div`
  text-align: right;
`;

const PriceLabel = styled.div`
  font-size: 11px;
  color: ${theme.colors.textTertiary};
`;

const PriceValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.primary};
`;

const QuoteButton = styled.button`
  background: #F97316;
  color: ${theme.colors.white};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 50px;
  transition: background 0.2s;
  &:hover { background: #EA580C; }
`;

const LangToggle = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 50px;
`;

const LangBtn = styled.button<{ $active: boolean }>`
  font-size: 13px;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active }) => $active ? theme.colors.white : theme.colors.textTertiary};
  &:hover {
    color: ${({ $active }) => $active ? theme.colors.white : theme.colors.textPrimary};
  }
`;

export default Header;
